import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { View, Text, FlatList, RefreshControl, StyleSheet } from 'react-native';
import { useRouter } from 'expo-router';
import { Bell } from 'lucide-react-native';
import { Header } from '@components/layout';
import { EmptyState } from '@components/common';
import { useTheme, SPACING, TYPOGRAPHY, CONTENT_MAX_WIDTH } from '../../src/theme';
import { useNotificationsStore } from '../../src/store/notificationsStore';
import { useAuthStore } from '../../src/store/authStore';
import { NotificationCard } from '../../src/components/notifications/NotificationCard';
import type { AppNotification, Timestamp } from '../../src/types';

// Group notifications into Today / Yesterday / Earlier buckets and flatten
// into a single list with inline section headers. Keeps the FlatList's
// pagination and recycling while giving the stream a sense of time.
type AlertRow = { kind: 'header'; label: string } | { kind: 'item'; notification: AppNotification };

function toDate(ts: Timestamp | undefined): Date {
  // Firestore Timestamp in the app; guard for already-Date or missing.
  const anyTs = ts as unknown as { toDate?: () => Date } | undefined;
  return anyTs?.toDate ? anyTs.toDate() : new Date(0);
}

function groupByDay(items: AppNotification[]): AlertRow[] {
  const now = new Date();
  const today = now.toDateString();
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  const yesterday = y.toDateString();

  const rows: AlertRow[] = [];
  let lastLabel: string | null = null;
  for (const n of items) {
    const ds = toDate(n.createdAt).toDateString();
    const label = ds === today ? 'Today' : ds === yesterday ? 'Yesterday' : 'Earlier';
    if (label !== lastLabel) {
      rows.push({ kind: 'header', label });
      lastLabel = label;
    }
    rows.push({ kind: 'item', notification: n });
  }
  return rows;
}

export default function AlertsScreen() {
  const { colors } = useTheme();
  const router = useRouter();
  const user = useAuthStore((s) => s.user);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const {
    notifications,
    unreadCount,
    isLoading,
    hasMore,
    fetchNotifications,
    fetchMore,
    markAsRead,
    markAllAsRead,
  } = useNotificationsStore(
    useShallow((s) => ({
      notifications: s.notifications,
      unreadCount: s.unreadCount,
      isLoading: s.isLoading,
      hasMore: s.hasMore,
      fetchNotifications: s.fetchNotifications,
      fetchMore: s.fetchMore,
      markAsRead: s.markAsRead,
      markAllAsRead: s.markAllAsRead,
    }))
  );

  // Fetch notifications on mount. The unread-count subscription lives in
  // app/_layout.tsx for the whole session — subscribing here too doubled
  // the listener (and the billed reads) whenever this tab was open.
  // Keyed on `user` only — store functions are stable and re-running on
  // their reference would tear down work every render.
  useEffect(() => {
    if (!user) return;
    void fetchNotifications(user.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Re-fetch the list whenever a new notification arrives (unreadCount increases)
  useEffect(() => {
    if (!user || unreadCount === 0) return;
    void fetchNotifications(user.id, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadCount]);

  const handleRefresh = useCallback(async () => {
    if (!user) return;
    setIsRefreshing(true);
    try {
      await fetchNotifications(user.id, true);
    } finally {
      setIsRefreshing(false);
    }
  }, [user, fetchNotifications]);

  const handleEndReached = useCallback(() => {
    if (!user || !hasMore || isLoading) return;
    void fetchMore(user.id);
  }, [user, hasMore, isLoading, fetchMore]);

  const handleMarkAllRead = useCallback(() => {
    if (!user) return;
    void markAllAsRead(user.id);
  }, [user, markAllAsRead]);

  const handleCardPress = useCallback(
    (notification: AppNotification) => {
      if (user) void markAsRead(notification.id, user.id);
      if (notification.data.postId) {
        router.push(`/post/${notification.data.postId}`);
      } else if (notification.data.structureId) {
        router.push(`/structure/${notification.data.structureId}`);
      }
    },
    [user, markAsRead, router]
  );

  const rows = useMemo(() => groupByDay(notifications), [notifications]);

  const renderItem = useCallback(
    ({ item }: { item: AlertRow }) => {
      if (item.kind === 'header') {
        return (
          <Text
            style={[styles.sectionHeader, { color: colors.textSecondary }]}
            testID="alerts-section-header">
            {item.label}
          </Text>
        );
      }
      return (
        <NotificationCard
          notification={item.notification}
          onPress={() => handleCardPress(item.notification)}
        />
      );
    },
    [handleCardPress, colors.textSecondary]
  );

  const keyExtractor = useCallback(
    (item: AlertRow) => (item.kind === 'header' ? `header-${item.label}` : item.notification.id),
    []
  );

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <Header title="Alerts" large rightLabel="Mark all read" onRightPress={handleMarkAllRead} />
      <FlatList
        data={rows}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        onEndReached={handleEndReached}
        onEndReachedThreshold={0.4}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
            colors={[colors.primary]}
          />
        }
        ListEmptyComponent={
          !isLoading ? (
            <EmptyState
              icon={Bell}
              title="No notifications yet"
              message="You'll see activity here when there's something new."
              style={styles.emptyState}
            />
          ) : null
        }
        contentContainerStyle={[
          styles.listContent,
          notifications.length === 0 && styles.emptyContainer,
        ]}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  // Match the (constrained) large Header: a 768 reading column centered in the
  // desktop content pane instead of cards stretched edge-to-edge. No-op on
  // mobile, where the screen is narrower than the cap.
  listContent: {
    width: '100%',
    maxWidth: CONTENT_MAX_WIDTH,
    alignSelf: 'center',
  },
  sectionHeader: {
    ...TYPOGRAPHY.overline,
    paddingHorizontal: SPACING.md,
    paddingTop: SPACING.md,
    paddingBottom: SPACING.xs,
  },
  emptyContainer: {
    flex: 1,
  },
  emptyState: {
    flex: 1,
  },
});
